import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { shortName } from "../../auth/scope";
import { formatInrPaise } from "../../domain/tiers";
import { listSubOperators } from "../../api/suboperators";
import { useProviderKind } from "./useProviderKind";
import { PinBuyersSection } from "./PinBuyerLinks";
import { GstChoiceField } from "../../components/GstChoiceField";
import { gstPriceSuffix } from "../../domain/gstChoice";
import {
  buyerKindFor,
  createPinPlan,
  getPinOffer,
  gstLabel,
  listPinPlans,
  paiseFromRupees,
  pinSplit,
  planKindForPublisher,
  rupeesText,
  updatePinPlan,
  type PinOffer,
  type PinPlan,
  type PinPlanAudience,
} from "../../api/pinPlans";

const inr = (paise: number) => formatInrPaise(paise / 100);

/**
 * Providers › Pin plans (owner, 2026-09-19).
 *
 * A PARENT publishes its pin plans here: speed ceiling, term, price per pin,
 * GST included or not, offered to all its PIN sub-operators or to named ones.
 * A PIN SUB-OPERATOR sees, read-only, the plans its parent offers it.
 *
 * Which view is decided by radaccess (/pinplans/offered says whether this
 * namespace is a sub-operator), never guessed from the namespace's shape.
 */
export function PinPlansScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const offer = useQuery({
    queryKey: ["pinoffer", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getPinOffer(identity, ns, signal),
    retry: false,
  });

  if (offer.isLoading) return <Typography color="text.secondary">Loading…</Typography>;
  if (offer.error) return <Alert severity="error">{offer.error instanceof Error ? offer.error.message : String(offer.error)}</Alert>;
  if (offer.data?.suboperator) return <OfferedPlans data={offer.data} />;
  return <PublishedPlans />;
}

function OfferedPlans({ data }: { data: PinOffer }) {
  const parent = data.parent ? shortName(data.parent) : "your parent operator";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Pin plans
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Offered to you by {parent}. One pin activates or renews one subscriber on a plan up to the pin&apos;s speed, for up to its term.
        </Typography>
      </Box>
      {data.suspended && <Alert severity="warning">{parent} has suspended your account. No pin plans are offered while it is suspended.</Alert>}
      {!data.suspended && data.kind !== "pin" && data.kind !== "site" && (
        <Alert severity="info">Pin plans are for operators that buy PINs. {parent} set you up as a different kind of provider.</Alert>
      )}
      {(data.kind === "pin" || data.kind === "site") && !data.suspended && data.plans.length === 0 && (
        <Alert severity="info">{parent} has not published a pin plan for you yet.</Alert>
      )}
      {data.kind === "site" && !data.suspended && (
        <Alert severity="info" variant="outlined">
          These are access pins: {parent} issues them to this site at no charge. Each pin activates one person for its term.
        </Alert>
      )}
      {data.plans.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Up to</TableCell>
                <TableCell align="right">Term</TableCell>
                <TableCell align="right">Price per pin</TableCell>
                <TableCell align="right">GST</TableCell>
                <TableCell align="right">You pay per pin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.plans.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">{p.speedmbps} Mbps</TableCell>
                  <TableCell align="right">{p.termdays} days</TableCell>
                  {p.kind === "access" ? (
                    <TableCell align="right" colSpan={3}>
                      No charge
                    </TableCell>
                  ) : (
                    <>
                      <TableCell align="right">{inr(p.netpaise)}</TableCell>
                      <TableCell align="right">
                        {inr(p.gstpaise)} ({gstLabel(p.gstratebp)})
                      </TableCell>
                      <TableCell align="right">
                        <strong>{inr(p.grosspaise)}</strong>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {data.kind !== "site" && (
        <Alert severity="info" variant="outlined">
          Buying pins comes next: choose a plan and a quantity, pay online, and the pins reach your account once {parent} approves.
        </Alert>
      )}
    </Box>
  );
}

interface Draft {
  id: string;
  name: string;
  speed: string;
  term: string;
  price: string;
  includesGST: boolean;
  gstBP: number;
  audience: PinPlanAudience;
  members: string[];
  active: boolean;
}

const emptyDraft: Draft = { id: "", name: "", speed: "100", term: "30", price: "", includesGST: true, gstBP: 1800, audience: "all", members: [], active: true };

function draftOf(p: PinPlan): Draft {
  return {
    id: p.id,
    name: p.name,
    speed: String(p.speedmbps),
    term: String(p.termdays),
    price: rupeesText(p.pricepaise),
    includesGST: p.priceincludesgst,
    gstBP: p.gstratebp,
    audience: p.audience,
    members: p.members,
    active: p.active,
  };
}

function PublishedPlans() {
  const identity = useIdentity();
  const parent = identity.namespace ?? "";
  const qc = useQueryClient();
  const plans = useQuery({
    queryKey: ["pinplans", parent],
    enabled: Boolean(identity.token && parent),
    queryFn: ({ signal }) => listPinPlans(identity, parent, signal),
    retry: false,
  });
  const subs = useQuery({
    queryKey: ["suboperators", parent],
    enabled: Boolean(identity.token && parent),
    queryFn: ({ signal }) => listSubOperators(identity, parent, signal),
    retry: false,
  });
  // A Network Operator publishes ACCESS plans to its sites; everyone else
  // publishes WHOLESALE plans to its PIN providers. Decided by the type, not
  // chosen: radaccess refuses the other kind (403). Unknown type = no form:
  // guessing wholesale offered a Network Operator's PIN child, not its sites.
  const providerType = useProviderKind();
  const kind = planKindForPublisher(providerType);
  const access = kind === "access";
  const holders = access ? "sites" : "PIN providers";
  const pinBuyers = kind ? (subs.data ?? []).filter((s) => s.kind === buyerKindFor(kind)) : [];
  const nameOf = (n: string) => pinBuyers.find((s) => s.namespace === n)?.legalname ?? shortName(n);

  const [draft, setDraft] = useState<Draft | null>(null);
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const speed = Number(draft?.speed ?? "");
  const term = Number(draft?.term ?? "");
  const pricePaise = draft ? paiseFromRupees(draft.price) : null;
  const speedOk = Number.isInteger(speed) && speed >= 1 && speed <= 100000;
  const termOk = Number.isInteger(term) && term >= 1 && term <= 400;
  const membersOk = draft?.audience !== "named" || draft.members.length > 0;
  const priceOk = access || (pricePaise !== null && pricePaise <= 10000000);
  const ready = Boolean(draft) && speedOk && termOk && priceOk && membersOk;
  const split = !access && pricePaise !== null && draft ? pinSplit(pricePaise, draft.includesGST, draft.gstBP) : null;

  const save = useMutation({
    mutationFn: (d: Draft) => {
      if (!kind) throw new Error("Your provider type is not known, so the plan kind is not known.");
      const input = {
        kind,
        name: d.name,
        speedmbps: speed,
        termdays: term,
        pricepaise: access ? 0 : pricePaise!,
        priceincludesgst: d.includesGST,
        gstratebp: d.gstBP,
        audience: d.audience,
        members: d.members,
        active: d.active,
      };
      return d.id ? updatePinPlan(identity, parent, d.id, input) : createPinPlan(identity, parent, input);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["pinplans", parent] });
      setDraft(null);
    },
  });

  const toggleActive = useMutation({
    mutationFn: (p: PinPlan) => {
      const d = draftOf(p);
      return updatePinPlan(identity, parent, p.id, {
        kind: p.kind ?? "wholesale",
        name: d.name,
        speedmbps: p.speedmbps,
        termdays: p.termdays,
        pricepaise: p.pricepaise,
        priceincludesgst: p.priceincludesgst,
        gstratebp: p.gstratebp,
        sac: p.sac,
        audience: p.audience,
        members: p.members,
        active: !p.active,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinplans", parent] }),
  });

  const list = plans.data ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" component="h1">
            Pin plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {access
              ? "The access pins your sites can order from you, at no charge. One pin activates one person on a plan up to its speed, for up to its term."
              : "What your PIN providers can buy from you. One pin activates or renews one subscriber on a plan up to its speed, for up to its term."}
          </Typography>
        </Box>
        <Button
          variant="contained"
          disabled={!kind}
          onClick={() => {
            save.reset();
            setDraft({ ...emptyDraft });
          }}
        >
          New pin plan
        </Button>
      </Box>

      {!kind && providerType && (
        <Alert severity="info">
          Only a licensed ISP or VNO sells wholesale pins: it issues the tax invoice for them. Buy your pins from your upstream ISP
          instead.
        </Alert>
      )}

      {!kind && !providerType && (
        <Alert severity="warning">
          Your provider type could not be read, so the console does not know whether you publish access pins (to sites) or wholesale
          pins (to PIN providers). Reload the page; if this stays, contact Ankh support.
        </Alert>
      )}

      {plans.error && <Alert severity="error">{plans.error instanceof Error ? plans.error.message : String(plans.error)}</Alert>}
      {toggleActive.error && <Alert severity="error">{toggleActive.error instanceof Error ? toggleActive.error.message : String(toggleActive.error)}</Alert>}
      {!plans.isLoading && !plans.error && list.length === 0 && (
        <Alert severity="info">
          No pin plans yet.{" "}
          {pinBuyers.length === 0
            ? access
              ? "Add a site first (one per hostel, library or lobby), then publish a plan for it."
              : "Add a provider of kind “Unlicensed operator (PIN)” first, then publish a plan for it."
            : `Publish one so your ${holders} can ${access ? "order pins" : "buy"}.`}
        </Alert>
      )}

      {list.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Up to</TableCell>
                <TableCell align="right">Term</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Net + GST</TableCell>
                <TableCell>Offered to</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id} sx={{ opacity: p.active ? 1 : 0.6 }}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">{p.speedmbps} Mbps</TableCell>
                  <TableCell align="right">{p.termdays} days</TableCell>
                  {p.kind === "access" ? (
                    <TableCell align="right" colSpan={2}>
                      Access pin · no charge
                    </TableCell>
                  ) : (
                    <>
                      <TableCell align="right">
                        {inr(p.pricepaise)}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {gstPriceSuffix({ gstratebp: p.gstratebp, priceincludesgst: p.priceincludesgst })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {inr(p.netpaise)} + {inr(p.gstpaise)}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {gstLabel(p.gstratebp)} · SAC {p.sac}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                  <TableCell>{p.audience === "all" ? `All ${holders}` : p.members.map(nameOf).join(", ")}</TableCell>
                  <TableCell>{p.active ? <Chip size="small" color="success" label="Published" /> : <Chip size="small" label="Withdrawn" />}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button
                      size="small"
                      onClick={() => {
                        save.reset();
                        setDraft(draftOf(p));
                      }}
                    >
                      Edit
                    </Button>
                    <Button size="small" disabled={toggleActive.isPending} onClick={() => toggleActive.mutate(p)}>
                      {p.active ? "Withdraw" : "Publish"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={draft !== null} onClose={() => setDraft(null)} fullWidth maxWidth="sm">
        <DialogTitle>{draft?.id ? "Edit pin plan" : "New pin plan"}</DialogTitle>
        {draft && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            <TextField label="Name (optional)" value={draft.name} onChange={(e) => set({ name: e.target.value })} helperText={`Blank: “${speedOk ? speed : "…"} Mbps · ${termOk ? term : "…"} days”`} />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Speed up to (Mbps)"
                value={draft.speed}
                onChange={(e) => set({ speed: e.target.value.replace(/\D/g, "") })}
                error={!speedOk}
                helperText="Covers every subscriber plan at or below this speed"
                sx={{ flex: 1, minWidth: 180 }}
              />
              <TextField
                label="Term (days)"
                value={draft.term}
                onChange={(e) => set({ term: e.target.value.replace(/\D/g, "") })}
                error={!termOk}
                helperText="Longest recharge one pin covers"
                sx={{ flex: 1, minWidth: 160 }}
              />
            </Box>
            {access ? (
              <Alert severity="info" icon={false}>
                Access pins carry no price and are never invoiced. You bill the campus for managing the network, not per pin.
              </Alert>
            ) : (
            <>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
              <TextField
                label="Price per pin (₹)"
                value={draft.price}
                onChange={(e) => set({ price: e.target.value })}
                error={draft.price !== "" && pricePaise === null}
                required
                sx={{ flex: 1, minWidth: 160 }}
              />
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <GstChoiceField
                  value={{ gstratebp: draft.gstBP, priceincludesgst: draft.includesGST }}
                  onChange={(c) => set({ gstBP: c.gstratebp, includesGST: c.priceincludesgst })}
                />
              </Box>
            </Box>
            {split && (
              <Alert severity="info" icon={false}>
                Per pin: {inr(split.net)} + GST {inr(split.gst)} = <strong>{inr(split.gross)}</strong>. 100 pins: {inr(split.gross * 100)}.
              </Alert>
            )}
            </>
            )}
            <RadioGroup value={draft.audience} onChange={(e) => set({ audience: e.target.value as PinPlanAudience })}>
              <FormControlLabel value="all" control={<Radio />} label={`Offer to all my ${holders}`} />
              <FormControlLabel value="named" control={<Radio />} label={`Offer only to these ${holders}`} />
            </RadioGroup>
            {draft.audience === "named" && (
              <Autocomplete
                multiple
                options={pinBuyers.map((s) => s.namespace)}
                value={draft.members}
                onChange={(_, v) => set({ members: v })}
                getOptionLabel={(n) => `${nameOf(n)} (${shortName(n)})`}
                renderInput={(params) => <TextField {...params} label={access ? "Sites" : "PIN providers"} error={!membersOk} helperText={pinBuyers.length === 0 ? `You have no ${holders} yet` : " "} />}
              />
            )}
            {draft.id && <FormControlLabel control={<Switch checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />} label="Published" />}
            {draft.id && (
              <Typography variant="caption" color="text.secondary">
                {access ? "Pins already issued keep the terms they were issued with." : "Pins already bought keep the price and terms they were bought at."}
              </Typography>
            )}
            {save.error && <Alert severity="error">{save.error instanceof Error ? save.error.message : String(save.error)}</Alert>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button variant="contained" disabled={!ready || save.isPending} onClick={() => draft && save.mutate(draft)}>
            {save.isPending ? "Saving…" : draft?.id ? "Save" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      {kind === "wholesale" && <PinBuyersSection seller={parent} />}
    </Box>
  );
}
